import { useEffect, useRef, useState } from "react";
import { FileIcon } from '../card/FileIcon';
import { useQuery } from "@tanstack/react-query";
import {
  ArchiveRestore,
  CheckSquare,
  ChevronRight,
  Download,
  Eye,
  FileText,
  FileType2,
  Folder,
  FolderInput,
  FolderPlus,
  History,
  Home,
  LayoutGrid,
  Link2,
  List,
  Loader2,
  MoreHorizontal,
  Pencil,
  Share2,
  Search,
  Square,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  LIBRARY_BULK_MAX,
  LIBRARY_MAX_DEPTH,
  LIBRARY_TRASH_DAYS,
  MEDIA_KIND_TEXT,
  type LibraryFile,
  type LibraryFolder,
  type LibraryUsage,
  type MediaKind,
} from "@kaba/shared";
import { api } from "../../lib/api";
import { accountPref, deviceClass, setAccountPref } from "../../lib/boardNav";
import {
  bulkLibrary,
  dateText,
  downloadZip,
  libraryPreviewItem,
  refreshLibrary,
  scopeBase,
  sizeText,
  uploadLibraryVersion,
  uploadToLibrary,
  useLibrary,
  ZIP_MAX_BYTES,
  type LibraryScope,
} from "../../lib/library";
import { navigate } from "../../lib/router";
import { PublishDialog } from "./PublishDialog";
import { toast } from "../../lib/toast";
import { AttachmentPreview } from "../card/AttachmentPreview";
import { PromptDialog } from "../ui/Prompt";
import {
  Button,
  cx,
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
  Sheet,
} from "../ui/ui";
import { FolderPicker } from "./FolderPicker";
import { VersionsDialog } from "./VersionsDialog";

type Pick = { file: LibraryFile; asCover: boolean };

const iconBtn =
  "inline-flex size-11 items-center justify-center rounded-lg sm:size-9";
const chip = (on: boolean) =>
  cx(
    "min-h-11 rounded-lg px-3 text-sm sm:min-h-9",
    on
      ? "bg-accent-soft font-semibold"
      : "hover:bg-black/5 dark:hover:bg-white/10",
  );

/**
 * Dateibibliothek eines Boards (0.54): Ordner mit Pfad, Suche über Unterordner, Galerie und Liste,
 * Hochladen (auch per Ziehen), Papierkorb. `mode="pick"` wählt eine Datei für eine Karte.
 * 0.55: Fassungen je Datei, Auswahlmodus mit Sammelaktionen und ZIP, Papierkorb mit Löschfrist.
 */
export function LibraryBrowser({
  scope,
  canEdit: canEditBoard = false,
  mode = "manage",
  onPick,
  onOpenCard,
  teamId: boardTeamIdProp,
  onOpenTeamLibrary,
  pickCover = false,
  pickLabel,
}: {
  /** 0.74: Auswahl nur für das Titelbild (Bilder, ohne Kästchen) */
  pickCover?: boolean;
  /** 0.89: Beschriftung der Auswahl (Standard „Zur Karte hinzufügen“) */
  pickLabel?: string;
  /** Nur Board-Ansicht der Team-Bibliothek: Team des Boards */
  teamId?: string | null;
  /** Board-Bibliothek, Team-Bibliothek oder (nur lesen) die für ein Board freigegebenen Team-Ordner */
  /** 0.72: statt in die Teameinstellungen zu wechseln, im Board auf die ganze Team-Bibliothek umschalten */
  onOpenTeamLibrary?: () => void;
  scope: LibraryScope;
  /** Nur Board-Bibliothek: Bearbeitungsrecht am Board (Team: kommt vom Server) */
  canEdit?: boolean;
  mode?: "manage" | "pick";
  onPick?: (p: Pick) => void;
  onOpenCard?: (id: string) => void;
}) {
  const picking = mode === "pick";
  const [folder, setFolder] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [type, setType] = useState<MediaKind>(pickCover ? "image" : "all");
  const [trash, setTrash] = useState(false);
  const [layout, setLayout] = useState<"grid" | "list">(() =>
    accountPref("libraryLayout", deviceClass() === "phone" ? "list" : "grid"),
  );
  const [preview, setPreview] = useState<number | null>(null);
  const [selected, setSelected] = useState<LibraryFile | null>(null);
  const [asCover, setAsCover] = useState(false);
  const [uploading, setUploading] = useState<string[]>([]);
  const [dialog, setDialog] = useState<
    | { kind: "newFolder" | "bulkMove" }
    | {
        kind: "renameFile" | "moveFile" | "usage" | "versions";
        file: LibraryFile;
      }
    | { kind: "renameFolder" | "moveFolder" | "publish"; folder: LibraryFolder }
    | null
  >(null);
  /** Auswahlmodus (0.55): `null` = aus */
  const [sel, setSel] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const versionInput = useRef<HTMLInputElement>(null);
  const versionFor = useRef<LibraryFile | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setAccountPref("libraryLayout", layout), [layout]);

  const data = useLibrary(scope, { folder, q: term, trash, type });
  const listing = data.data;
  const canEdit =
    scope.kind === "board"
      ? canEditBoard
      : scope.kind === "team"
        ? !!listing?.canEdit
        : false;
  const canPublish = scope.kind === "team" && !!listing?.canPublish;
  const boardId = scope.kind === "team" ? null : scope.boardId;
  /** Board-Ansicht der Team-Bibliothek: Team des Boards (für den Link in die Teameinstellungen) */
  const boardTeamId = boardTeamIdProp ?? null;
  const searching = !!term || trash;
  const folders = searching ? [] : (listing?.folders ?? []);
  const files = listing?.files ?? [];
  const path = listing?.path ?? [];
  const editable = canEdit && !picking;

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      await refreshLibrary(scope);
      if (ok) toast.info(ok);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const upload = async (list: FileList | File[]) => {
    const arr = [...list];
    setUploading((u) => [...u, ...arr.map((f) => f.name)]);
    for (const f of arr) {
      try {
        await uploadToLibrary(scope, f, folder);
      } catch (err) {
        toast.error(`${f.name}: ${(err as Error).message}`);
      } finally {
        setUploading((u) => {
          const i = u.indexOf(f.name);
          return i < 0 ? u : [...u.slice(0, i), ...u.slice(i + 1)];
        });
      }
    }
    await refreshLibrary(scope);
  };
  const go = (id: string | null) => {
    setFolder(id);
    setQ("");
    setTerm("");
    setTrash(false);
    setSelected(null);
    setSel(null);
  };
  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s ?? []);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const choose = (f: LibraryFile) => {
    if (sel) toggle(f.id);
    else if (picking) {
      setSelected((s) => (s?.id === f.id ? null : f));
      setAsCover(false);
    } else setPreview(files.indexOf(f));
  };

  const newVersion = (f: LibraryFile) => {
    versionFor.current = f;
    versionInput.current?.click();
  };
  const sendVersion = async (file: File) => {
    const f = versionFor.current;
    versionFor.current = null;
    if (!f) return;
    setUploading((u) => [...u, file.name]);
    try {
      const r = await uploadLibraryVersion(f.id, file);
      await refreshLibrary(scope);
      toast.info(
        r.usage
          ? `Fassung ${r.version} von „${r.name}“ ist aktuell. Karten mit älterer Fassung aktualisierst du unter „Verwendung“.`
          : `Fassung ${r.version} von „${r.name}“ ist aktuell.`,
      );
    } catch (err) {
      toast.error(`${file.name}: ${(err as Error).message}`);
    } finally {
      setUploading((u) => {
        const i = u.indexOf(file.name);
        return i < 0 ? u : [...u.slice(0, i), ...u.slice(i + 1)];
      });
    }
  };

  const chosen = sel ? files.filter((f) => sel.has(f.id)) : [];
  const allChosen = !!sel && files.length > 0 && chosen.length === files.length;
  const bulk = async (
    action: "move" | "trash" | "restore" | "delete",
    ids: string[],
    ok: string,
    folderId?: string | null,
  ) => {
    setBusy(true);
    try {
      await bulkLibrary(scope, action, ids, folderId);
      setSel(null);
      toast.info(ok);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
      await refreshLibrary(scope);
    }
  };
  const zip = (list: LibraryFile[]) => {
    if (list.length > LIBRARY_BULK_MAX)
      return toast.error(
        `Höchstens ${LIBRARY_BULK_MAX} Dateien auf einmal herunterladen.`,
      );
    if (list.reduce((n, f) => n + f.size, 0) > ZIP_MAX_BYTES)
      return toast.error(
        "Zusammen zu groß für ein ZIP. Wähle weniger Dateien.",
      );
    downloadZip(
      scope,
      list.map((f) => f.id),
    );
  };
  const n = (k: number) => (k === 1 ? "1 Datei" : `${k} Dateien`);
  const purgeNote = (list: LibraryFile[]) => {
    const used = list.reduce((k, f) => k + f.usage, 0);
    return used
      ? " Karten, die sie nutzen, behalten ihre Anhänge."
      : "";
  };

  const fileMenu = (f: LibraryFile) => (
    <DropdownMenu modal={false}>
      <DropdownTrigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für „${f.name}“`}
          className={cx(
            iconBtn,
            "shrink-0 text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10",
          )}
        >
          <MoreHorizontal size={18} aria-hidden />
        </button>
      </DropdownTrigger>
      <DropdownContent>
        <DropdownItem
          icon={<Eye size={16} />}
          onSelect={() => setPreview(files.indexOf(f))}
        >
          Ansehen
        </DropdownItem>
        <DropdownItem
          icon={<Download size={16} />}
          onSelect={() =>
            location.assign(`/api/library/files/${f.id}/download`)
          }
        >
          Herunterladen
        </DropdownItem>
        <DropdownItem
          icon={<Link2 size={16} />}
          onSelect={() => setDialog({ kind: "usage", file: f })}
        >
          Verwendung{f.usage ? ` (${f.usage})` : ""}
        </DropdownItem>
        {f.version > 1 && (
          <DropdownItem
            icon={<History size={16} />}
            onSelect={() => setDialog({ kind: "versions", file: f })}
          >
            Fassungen ({f.version})
          </DropdownItem>
        )}
        {editable && !f.trashedAt && (
          <>
            <DropdownSeparator />
            <DropdownItem
              icon={<Pencil size={16} />}
              onSelect={() => setDialog({ kind: "renameFile", file: f })}
            >
              Umbenennen
            </DropdownItem>
            <DropdownItem
              icon={<Upload size={16} />}
              onSelect={() => newVersion(f)}
            >
              Neue Fassung hochladen …
            </DropdownItem>
            <DropdownItem
              icon={<FolderInput size={16} />}
              onSelect={() => setDialog({ kind: "moveFile", file: f })}
            >
              Verschieben nach …
            </DropdownItem>
            <DropdownItem
              icon={<Trash2 size={16} />}
              onSelect={() =>
                void run(
                  () => api.post(`/api/library/files/${f.id}/trash`),
                  `„${f.name}“ liegt im Papierkorb.`,
                )
              }
            >
              In den Papierkorb
            </DropdownItem>
          </>
        )}
        {editable && f.trashedAt && (
          <>
            <DropdownSeparator />
            <DropdownItem
              icon={<ArchiveRestore size={16} />}
              onSelect={() =>
                void run(
                  () => api.post(`/api/library/files/${f.id}/restore`),
                  `„${f.name}“ ist wiederhergestellt.`,
                )
              }
            >
              Wiederherstellen
            </DropdownItem>
            <DropdownItem
              danger
              icon={<Trash2 size={16} />}
              onSelect={() => {
                const note = f.usage
                  ? ` Die ${f.usage === 1 ? "Karte, die sie nutzt, behält" : `${f.usage} Karten, die sie nutzen, behalten`} ihren Anhang.`
                  : "";
                const vers =
                  f.version > 1 ? " Alle älteren Fassungen werden mitgelöscht." : "";
                if (
                  window.confirm(
                    `„${f.name}“ endgültig aus der Bibliothek löschen?${vers}${note}`,
                  )
                )
                  void run(
                    () => api.del(`/api/library/files/${f.id}`),
                    "Endgültig gelöscht.",
                  );
              }}
            >
              Endgültig löschen
            </DropdownItem>
          </>
        )}
      </DropdownContent>
    </DropdownMenu>
  );

  const publishText = (f: LibraryFolder) =>
    !f.publish || f.publish.mode === "none"
      ? ""
      : f.publish.mode === "all"
        ? "für alle Boards freigegeben"
        : `für ${f.publish.boardIds.length === 1 ? "1 Board" : `${f.publish.boardIds.length} Boards`} freigegeben`;
  const folderMenu = (f: LibraryFolder) =>
    editable || (canPublish && !picking && f.publish) ? (
      <DropdownMenu modal={false}>
        <DropdownTrigger asChild>
          <button
            type="button"
            aria-label={`Aktionen für Ordner „${f.name}“`}
            className={cx(
              iconBtn,
              "shrink-0 text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10",
            )}
          >
            <MoreHorizontal size={18} aria-hidden />
          </button>
        </DropdownTrigger>
        <DropdownContent>
          {canPublish && f.publish && (
            <DropdownItem
              icon={<Share2 size={16} />}
              onSelect={() => setDialog({ kind: "publish", folder: f })}
            >
              Für Boards freigeben …
            </DropdownItem>
          )}
          {editable && (
            <>
              {canPublish && f.publish && <DropdownSeparator />}
              <DropdownItem
                icon={<Pencil size={16} />}
                onSelect={() => setDialog({ kind: "renameFolder", folder: f })}
              >
                Umbenennen
              </DropdownItem>
              <DropdownItem
                icon={<FolderInput size={16} />}
                onSelect={() => setDialog({ kind: "moveFolder", folder: f })}
              >
                Verschieben nach …
              </DropdownItem>
              <DropdownItem
                danger
                icon={<Trash2 size={16} />}
                onSelect={() => {
                  if (f.files || f.folders)
                    return toast.error(
                      "Der Ordner ist nicht leer. Verschiebe oder lösche zuerst seinen Inhalt.",
                    );
                  void run(
                    () => api.del(`/api/library/folders/${f.id}`),
                    `Ordner „${f.name}“ gelöscht.`,
                  );
                }}
              >
                Löschen
              </DropdownItem>
            </>
          )}
        </DropdownContent>
      </DropdownMenu>
    ) : null;

  const thumb = (f: LibraryFile, cls: string) =>
    f.isImage && f.thumbUrl ? (
      <img
        src={f.thumbUrl}
        alt=""
        loading="lazy"
        decoding="async"
        className={cx(cls, "object-cover")}
        style={{
          backgroundImage: f.placeholder ? `url(${f.placeholder})` : undefined,
          backgroundSize: "cover",
        }}
      />
    ) : (
      <span
        className={cx(
          cls,
          "flex items-center justify-center bg-[var(--column)] text-[var(--ink-soft)]",
        )}
      >
        <FileIcon mime={f.mime} filename={f.name} size={28} />
      </span>
    );
  const meta = (f: LibraryFile) =>
    f.trashedAt && f.purgeAt ? (
      <>
        {sizeText(f.size)} · wird am {dateText(f.purgeAt)} gelöscht
      </>
    ) : (
      <>
        {sizeText(f.size)} · {dateText(f.updatedAt)}
        {f.version > 1 ? ` · Fassung ${f.version}` : ""}
        {f.usage
          ? ` · in ${f.usage === 1 ? "1 Karte" : `${f.usage} Karten`}`
          : ""}
      </>
    );
  const mark = (f: LibraryFile, cls: string) =>
    sel ? (
      <span
        aria-hidden
        className={cx(
          "pointer-events-none flex size-6 items-center justify-center rounded-md",
          sel.has(f.id)
            ? "bg-accent text-on-accent"
            : "border border-[var(--line)] bg-[var(--panel)] text-transparent",
          cls,
        )}
      >
        <CheckSquare size={16} />
      </span>
    ) : null;
  const tileLabel = (f: LibraryFile) =>
    sel
      ? `${f.name} ${sel.has(f.id) ? "abwählen" : "auswählen"}`
      : picking
        ? `${f.name} auswählen`
        : `Vorschau: ${f.name}`;

  const empty =
    !data.isLoading && !data.isError && !folders.length && !files.length;

  return (
    <div
      className="flex flex-col gap-3"
      onDragOver={(e) => {
        if (editable && !trash && e.dataTransfer.types.includes("Files"))
          e.preventDefault();
      }}
      onDrop={(e) => {
        if (!editable || trash || !e.dataTransfer.files.length) return;
        e.preventDefault();
        void upload(e.dataTransfer.files);
      }}
    >
      <div className="flex flex-col gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <nav
            aria-label="Ordnerpfad"
            className="flex min-w-0 flex-1 basis-full flex-wrap items-center gap-0.5 text-sm sm:basis-auto"
          >
            <button
              type="button"
              onClick={() => go(null)}
              aria-current={!folder && !searching ? "location" : undefined}
              className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 font-medium hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/10"
            >
              <Home size={15} aria-hidden /> Bibliothek
            </button>
            {!searching &&
              path.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-0.5">
                  <ChevronRight
                    size={14}
                    aria-hidden
                    className="text-[var(--ink-soft)]"
                  />
                  <button
                    type="button"
                    onClick={() => go(p.id)}
                    aria-current={p.id === folder ? "location" : undefined}
                    className="min-h-11 rounded-lg px-2 hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/10"
                  >
                    {p.name}
                  </button>
                </span>
              ))}
            {trash && (
              <span className="inline-flex items-center gap-0.5">
                <ChevronRight
                  size={14}
                  aria-hidden
                  className="text-[var(--ink-soft)]"
                />{" "}
                Papierkorb
              </span>
            )}
          </nav>
          {editable && !searching && path.length < LIBRARY_MAX_DEPTH && (
            <Button
              variant="ghost"
              onClick={() => setDialog({ kind: "newFolder" })}
            >
              <FolderPlus size={16} aria-hidden /> Neuer Ordner
            </Button>
          )}
          {canEdit && !trash && (
            <Button
              variant={picking ? "ghost" : "primary"}
              onClick={() => input.current?.click()}
            >
              <Upload size={16} aria-hidden /> Hochladen
            </Button>
          )}
          <input
            ref={input}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void upload(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={versionInput}
            type="file"
            hidden
            aria-hidden
            tabIndex={-1}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void sendVersion(file);
              else versionFor.current = null;
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex min-w-0 flex-1 basis-56 items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2">
            <Search
              size={16}
              className="shrink-0 text-[var(--ink-soft)]"
              aria-hidden
            />
            <input
              type="search"
              aria-label="In der Bibliothek suchen (auch in Unterordnern)"
              placeholder="Dateiname suchen …"
              className="min-h-11 min-w-0 flex-1 bg-transparent text-base outline-none sm:min-h-9 sm:text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <div
            role="group"
            aria-label="Art der Datei"
            className="flex flex-wrap gap-1"
          >
            {(Object.keys(MEDIA_KIND_TEXT) as MediaKind[]).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={type === k}
                onClick={() => setType(k)}
                className={chip(type === k)}
              >
                {MEDIA_KIND_TEXT[k]}
              </button>
            ))}
          </div>
          {editable && (
            <button
              type="button"
              aria-pressed={trash}
              onClick={() =>
                trash
                  ? go(folder)
                  : (setTrash(true), setSelected(null), setSel(null))
              }
              className={cx(chip(trash), "inline-flex items-center gap-1.5")}
            >
              <Trash2 size={15} aria-hidden /> Papierkorb
            </button>
          )}
          {!picking && scope.kind !== "boardTeam" && files.length > 0 && (
            <button
              type="button"
              aria-pressed={!!sel}
              onClick={() => setSel((s) => (s ? null : new Set()))}
              className={cx(chip(!!sel), "inline-flex items-center gap-1.5")}
            >
              <CheckSquare size={15} aria-hidden /> Auswählen
            </button>
          )}
          <div
            role="group"
            aria-label="Darstellung"
            className="ml-auto flex gap-1"
          >
            <button
              type="button"
              aria-pressed={layout === "list"}
              aria-label="Als Liste"
              onClick={() => setLayout("list")}
              className={cx(
                iconBtn,
                layout === "list"
                  ? "bg-accent-soft"
                  : "hover:bg-black/5 dark:hover:bg-white/10",
              )}
            >
              <List size={17} aria-hidden />
            </button>
            <button
              type="button"
              aria-pressed={layout === "grid"}
              aria-label="Als Galerie"
              onClick={() => setLayout("grid")}
              className={cx(
                iconBtn,
                layout === "grid"
                  ? "bg-accent-soft"
                  : "hover:bg-black/5 dark:hover:bg-white/10",
              )}
            >
              <LayoutGrid size={17} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      <p
        className="px-1 text-sm text-[var(--ink-soft)]"
        role="status"
        aria-live="polite"
      >
        {data.isError
          ? "Die Bibliothek konnte nicht geladen werden."
          : data.isLoading
            ? "Lädt …"
            : trash
              ? `${files.length} ${files.length === 1 ? "Datei" : "Dateien"} im Papierkorb – nach ${LIBRARY_TRASH_DAYS} Tagen werden sie endgültig gelöscht. Karten behalten ihre Anhänge.`
              : `${folders.length ? `${folders.length} ${folders.length === 1 ? "Ordner" : "Ordner"}, ` : ""}${files.length} ${files.length === 1 ? "Datei" : "Dateien"}${term ? " gefunden (auch in Unterordnern)" : ""}`}
      </p>
      {trash && editable && files.length > 0 && !sel && (
        <div className="px-1">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Papierkorb leeren? ${n(files.length)} werden endgültig gelöscht, samt älterer Fassungen.${purgeNote(files)}`,
                )
              )
                void bulk(
                  "delete",
                  files.map((f) => f.id),
                  "Papierkorb geleert.",
                );
            }}
          >
            <Trash2 size={16} aria-hidden /> Papierkorb leeren
          </Button>
        </div>
      )}
      {uploading.map((n, i) => (
        <p
          key={`${n}-${i}`}
          className="flex items-center gap-2 px-1 text-sm text-[var(--ink-soft)]"
          role="status"
        >
          <Loader2 size={15} className="animate-spin" aria-hidden /> {n} wird
          hochgeladen …
        </p>
      ))}

      {empty && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[var(--line)] p-8 text-center text-sm text-[var(--ink-soft)]">
          {searching || type !== "all" ? (
            trash ? (
              "Der Papierkorb ist leer."
            ) : (
              "Keine passenden Dateien."
            )
          ) : folder ? (
            "Dieser Ordner ist leer."
          ) : scope.kind === "boardTeam" ? (
            <>
              <p className="font-semibold text-[var(--ink)]">
                Noch nichts aus der Team-Bibliothek freigegeben
              </p>
              <p>
                Admins des Teams geben Ordner der Team-Bibliothek für Boards
                frei – dann erscheinen sie hier und lassen sich in Karten
                verwenden.
              </p>
              {listing?.teamMember && boardTeamId && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    onOpenTeamLibrary
                      ? onOpenTeamLibrary()
                      : navigate(`/teams/${boardTeamId}?tab=bibliothek`)
                  }
                >
                  Team-Bibliothek öffnen
                </Button>
              )}
            </>
          ) : scope.kind === "team" ? (
            <>
              <p className="font-semibold text-[var(--ink)]">
                Dateien für das ganze Team ablegen
              </p>
              <p>
                Logos, Vorlagen, Satzung – einmal hochladen und in den Boards
                des Teams verwenden. Lege Ordner an und gib sie über ⋯ für
                Boards frei; Dateien auf der obersten Ebene bleiben im Team.
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-[var(--ink)]">
                Dateien für dieses Board dauerhaft ablegen
              </p>
              <p>
                Logos, Vorlagen, Fotos – einmal hochladen und in beliebig vielen
                Karten verwenden.
                {canEdit
                  ? " Hierher ziehen oder „Hochladen“. Dateien aus Karten legst du am Anhang mit „In Bibliothek“ ab."
                  : ""}
              </p>
            </>
          )}
        </div>
      )}

      {layout === "grid" ? (
        <>
          {folders.length > 0 && (
            <ul
              className="grid grid-cols-1 gap-2 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              aria-label="Ordner"
            >
              {folders.map((f) => (
                <li
                  key={f.id}
                  className="flex min-w-0 items-center gap-1 rounded-xl border border-[var(--line)] bg-[var(--panel)] pr-1"
                >
                  <button
                    type="button"
                    onClick={() => go(f.id)}
                    className="flex min-h-14 min-w-0 flex-1 items-center gap-2 p-2 text-left"
                    aria-label={`Ordner „${f.name}“ öffnen`}
                  >
                    <Folder
                      size={22}
                      aria-hidden
                      className="shrink-0 text-accent-fg"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {f.name}
                      </span>
                      <span className="block text-xs text-[var(--ink-soft)]">
                        {f.files} {f.files === 1 ? "Datei" : "Dateien"}
                        {f.folders ? ` · ${f.folders} Ordner` : ""}
                      </span>
                      {publishText(f) && (
                        <span className="flex items-center gap-1 text-xs font-medium text-accent-fg">
                          <Share2 size={12} aria-hidden /> {publishText(f)}
                        </span>
                      )}
                    </span>
                  </button>
                  {folderMenu(f)}
                </li>
              ))}
            </ul>
          )}
          <ul
            className="grid grid-cols-2 gap-3 empty:hidden min-[480px]:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
            aria-label="Dateien"
          >
            {files.map((f) => (
              <li
                key={f.id}
                className={cx(
                  "flex min-w-0 flex-col overflow-hidden rounded-xl border bg-[var(--panel)]",
                  selected?.id === f.id || sel?.has(f.id)
                    ? "border-accent ring-2 ring-accent/40"
                    : "border-[var(--line)]",
                )}
              >
                <button
                  type="button"
                  onClick={() => choose(f)}
                  aria-pressed={
                    sel
                      ? sel.has(f.id)
                      : picking
                        ? selected?.id === f.id
                        : undefined
                  }
                  className="relative block aspect-[4/3] w-full"
                  aria-label={tileLabel(f)}
                >
                  {thumb(f, "size-full")}
                  {mark(f, "absolute left-2 top-2")}
                </button>
                <div className="flex min-w-0 items-start gap-1 p-2">
                  <div className="min-w-0 flex-1">
                    <span
                      className="block truncate text-sm font-medium"
                      title={f.name}
                    >
                      {f.name}
                    </span>
                    <span className="block truncate text-xs text-[var(--ink-soft)]">
                      {f.path !== undefined && !f.trashedAt
                        ? f.path || "Bibliothek"
                        : meta(f)}
                    </span>
                  </div>
                  {!picking && !sel && fileMenu(f)}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <ul
          className="flex flex-col divide-y divide-[var(--line)] overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)] empty:hidden"
          aria-label="Ordner und Dateien"
        >
          {folders.map((f) => (
            <li key={f.id} className="flex items-center gap-2 pr-1">
              <button
                type="button"
                onClick={() => go(f.id)}
                className="flex min-h-14 min-w-0 flex-1 items-center gap-3 px-3 text-left"
                aria-label={`Ordner „${f.name}“ öffnen`}
              >
                <Folder
                  size={22}
                  aria-hidden
                  className="shrink-0 text-accent-fg"
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {f.name}
                </span>
                <span className="shrink-0 text-right text-xs text-[var(--ink-soft)]">
                  {f.files} {f.files === 1 ? "Datei" : "Dateien"}
                  {publishText(f) && (
                    <span className="flex items-center justify-end gap-1 font-medium text-accent-fg">
                      <Share2 size={12} aria-hidden /> {publishText(f)}
                    </span>
                  )}
                </span>
              </button>
              {folderMenu(f)}
            </li>
          ))}
          {files.map((f) => (
            <li
              key={f.id}
              className={cx(
                "flex items-center gap-3 px-2 py-1.5",
                (selected?.id === f.id || sel?.has(f.id)) && "bg-accent-soft/60",
              )}
            >
              <button
                type="button"
                onClick={() => choose(f)}
                aria-pressed={
                  sel
                    ? sel.has(f.id)
                    : picking
                      ? selected?.id === f.id
                      : undefined
                }
                className="flex min-h-12 min-w-0 flex-1 items-center gap-3 text-left"
                aria-label={tileLabel(f)}
              >
                {mark(f, "shrink-0")}
                {thumb(f, "size-11 shrink-0 rounded-lg")}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {f.name}
                  </span>
                  <span className="block truncate text-xs text-[var(--ink-soft)]">
                    {f.path !== undefined ? `${f.path || "Bibliothek"} · ` : ""}
                    {meta(f)}
                  </span>
                </span>
              </button>
              {!picking && !sel && fileMenu(f)}
            </li>
          ))}
        </ul>
      )}

      {sel && (
        <div
          role="toolbar"
          aria-label="Auswahl"
          className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2.5 shadow-lg"
        >
          <span
            className="min-w-0 flex-1 basis-full text-sm font-medium sm:basis-auto"
            aria-live="polite"
          >
            {chosen.length ? `${n(chosen.length)} ausgewählt` : "Dateien antippen zum Auswählen"}
          </span>
          <Button
            variant="ghost"
            onClick={() =>
              setSel(allChosen ? new Set() : new Set(files.map((f) => f.id)))
            }
          >
            <Square size={16} aria-hidden />{" "}
            {allChosen ? "Keine" : "Alle"}
          </Button>
          <Button
            variant="ghost"
            disabled={!chosen.length}
            onClick={() => zip(chosen)}
          >
            <Download size={16} aria-hidden /> ZIP
          </Button>
          {editable && !trash && (
            <>
              <Button
                variant="ghost"
                disabled={!chosen.length || busy}
                onClick={() => setDialog({ kind: "bulkMove" })}
              >
                <FolderInput size={16} aria-hidden /> Verschieben
              </Button>
              <Button
                variant="ghost"
                disabled={!chosen.length || busy}
                onClick={() =>
                  void bulk(
                    "trash",
                    chosen.map((f) => f.id),
                    `${n(chosen.length)} im Papierkorb.`,
                  )
                }
              >
                <Trash2 size={16} aria-hidden /> Papierkorb
              </Button>
            </>
          )}
          {editable && trash && (
            <>
              <Button
                variant="ghost"
                disabled={!chosen.length || busy}
                onClick={() =>
                  void bulk(
                    "restore",
                    chosen.map((f) => f.id),
                    `${n(chosen.length)} wiederhergestellt.`,
                  )
                }
              >
                <ArchiveRestore size={16} aria-hidden /> Wiederherstellen
              </Button>
              <Button
                variant="ghost"
                disabled={!chosen.length || busy}
                onClick={() => {
                  if (
                    window.confirm(
                      `${n(chosen.length)} endgültig löschen, samt älterer Fassungen?${purgeNote(chosen)}`,
                    )
                  )
                    void bulk(
                      "delete",
                      chosen.map((f) => f.id),
                      "Endgültig gelöscht.",
                    );
                }}
              >
                <Trash2 size={16} aria-hidden /> Endgültig löschen
              </Button>
            </>
          )}
          <button
            type="button"
            aria-label="Auswahl beenden"
            onClick={() => setSel(null)}
            className={cx(iconBtn, "hover:bg-black/5 dark:hover:bg-white/10")}
          >
            <X size={18} aria-hidden />
          </button>
        </div>
      )}

      {picking && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 shadow-lg">
          <span className="min-w-0 flex-1 truncate text-sm">
            {selected ? <b>{selected.name}</b> : "Datei auswählen …"}
          </span>
          {selected && pickCover && !selected.isImage && (
            <span className="text-xs text-amber-800 dark:text-amber-200">Als Titelbild eignen sich nur Bilder.</span>
          )}
          {selected?.isImage && !pickCover && (
            <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-9">
              <input
                type="checkbox"
                className="size-5 accent-[var(--color-accent)]"
                checked={asCover}
                onChange={(e) => setAsCover(e.target.checked)}
              />
              Als Titelbild verwenden
            </label>
          )}
          <Button
            variant="primary"
            disabled={!selected || (pickCover && !selected.isImage)}
            onClick={() => selected && onPick?.({ file: selected, asCover: pickCover || asCover })}
          >
            {pickCover ? "Als Titelbild verwenden" : (pickLabel ?? "Zur Karte hinzufügen")}
          </Button>
        </div>
      )}

      <AttachmentPreview
        items={files.map(libraryPreviewItem)}
        index={preview}
        onIndex={setPreview}
        onClose={() => setPreview(null)}
      />
      <PromptDialog
        open={dialog?.kind === "newFolder"}
        title="Neuer Ordner"
        label="Name"
        confirm="Anlegen"
        onClose={() => setDialog(null)}
        onSubmit={(name) => {
          setDialog(null);
          if (name.trim())
            void run(() =>
              api.post(`${scopeBase(scope)}/folders`, {
                name: name.trim(),
                parentId: folder,
              }),
            );
        }}
      />
      <PromptDialog
        open={dialog?.kind === "renameFile" || dialog?.kind === "renameFolder"}
        title={
          dialog?.kind === "renameFolder"
            ? "Ordner umbenennen"
            : "Datei umbenennen"
        }
        label="Name"
        initial={
          dialog?.kind === "renameFile"
            ? dialog.file.name
            : dialog?.kind === "renameFolder"
              ? dialog.folder.name
              : ""
        }
        hint={
          dialog?.kind === "renameFile" && dialog.file.usage
            ? "Karten, die die Datei schon nutzen, behalten ihren bisherigen Namen."
            : undefined
        }
        onClose={() => setDialog(null)}
        onSubmit={(name) => {
          const d = dialog;
          setDialog(null);
          if (!name.trim() || !d) return;
          if (d.kind === "renameFile")
            void run(() =>
              api.patch(`/api/library/files/${d.file.id}`, {
                name: name.trim(),
              }),
            );
          if (d.kind === "renameFolder")
            void run(() =>
              api.patch(`/api/library/folders/${d.folder.id}`, {
                name: name.trim(),
              }),
            );
        }}
      />
      <FolderPicker
        open={
          dialog?.kind === "moveFile" ||
          dialog?.kind === "moveFolder" ||
          dialog?.kind === "bulkMove"
        }
        scope={
          scope.kind === "boardTeam"
            ? { kind: "board", boardId: scope.boardId }
            : scope
        }
        title={
          dialog?.kind === "moveFolder"
            ? `Ordner „${dialog.folder.name}“ verschieben`
            : dialog?.kind === "moveFile"
              ? `„${dialog.file.name}“ verschieben`
              : dialog?.kind === "bulkMove"
                ? `${n(chosen.length)} verschieben`
                : "Verschieben"
        }
        confirm="Hierher verschieben"
        start={folder}
        exclude={dialog?.kind === "moveFolder" ? dialog.folder.id : undefined}
        onClose={() => setDialog(null)}
        onPick={(target) => {
          const d = dialog;
          setDialog(null);
          if (d?.kind === "bulkMove")
            void bulk(
              "move",
              chosen.map((f) => f.id),
              `${n(chosen.length)} verschoben.`,
              target,
            );
          if (d?.kind === "moveFile")
            void run(
              () =>
                api.patch(`/api/library/files/${d.file.id}`, {
                  folderId: target,
                }),
              "Verschoben.",
            );
          if (d?.kind === "moveFolder")
            void run(
              () =>
                api.patch(`/api/library/folders/${d.folder.id}`, {
                  parentId: target,
                }),
              "Verschoben.",
            );
        }}
      />
      <UsageDialog
        file={dialog?.kind === "usage" ? dialog.file : null}
        scope={scope}
        boardId={boardId}
        canEdit={editable}
        onClose={() => setDialog(null)}
        onOpenCard={onOpenCard}
      />
      <PublishDialog
        folder={dialog?.kind === "publish" ? dialog.folder : null}
        teamId={scope.kind === "team" ? scope.teamId : null}
        onClose={() => setDialog(null)}
        onSaved={() => void refreshLibrary(scope)}
      />
      <VersionsDialog
        file={dialog?.kind === "versions" ? dialog.file : null}
        scope={scope}
        canEdit={editable}
        onNewVersion={(f) => {
          setDialog(null);
          newVersion(f);
        }}
        onClose={() => setDialog(null)}
      />
    </div>
  );
}

function UsageDialog({
  file,
  scope,
  boardId,
  canEdit,
  onClose,
  onOpenCard,
}: {
  file: LibraryFile | null;
  scope: LibraryScope;
  /** Board, in dem die Bibliothek gerade offen ist (Karten anderer Boards öffnen über die Adresse) */
  boardId: string | null;
  canEdit: boolean;
  onClose: () => void;
  onOpenCard?: (id: string) => void;
}) {
  const q = useQuery({
    queryKey: ["library-usage", file?.id, file?.version],
    queryFn: () =>
      api.get<LibraryUsage>(`/api/library/files/${file!.id}/usage`),
    enabled: !!file,
  });
  const [busy, setBusy] = useState(false);
  const cards = q.data?.cards ?? [];
  const older = cards.filter((c) => !c.current);
  const update = async (attachmentIds?: string[]) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.post<{
        updated: number;
        cards: number;
        skipped: number;
      }>(
        `/api/library/files/${file.id}/update-attachments`,
        attachmentIds ? { attachmentIds } : {},
      );
      const skipped = r.skipped
        ? ` ${r.skipped === 1 ? "1 Anhang liegt" : `${r.skipped} Anhänge liegen`} in Boards, die du nicht bearbeiten darfst.`
        : "";
      if (r.updated)
        toast.info(
          `${r.cards === 1 ? "1 Karte zeigt" : `${r.cards} Karten zeigen`} jetzt Fassung ${file.version}.${skipped}`,
        );
      else if (skipped) toast.error(`Nichts aktualisiert.${skipped}`);
      else toast.info("Schon alles aktuell.");
      await q.refetch();
      await refreshLibrary(scope);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet
      open={!!file}
      onOpenChange={(o) => !o && onClose()}
      title={file ? `Verwendung von „${file.name}“` : "Verwendung"}
      side="center"
    >
      <div className="flex flex-col gap-3 p-4">
        {q.isLoading ? (
          <p className="text-sm text-[var(--ink-soft)]">Lädt …</p>
        ) : cards.length ? (
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
            {cards.map((c) => (
              <li
                key={c.attachmentId}
                className="flex flex-wrap items-center gap-2 px-3 py-1.5"
              >
                <span className="min-w-0 flex-1 text-sm">
                  <span className="block truncate">{c.title}</span>
                  <span className="block text-xs text-[var(--ink-soft)]">
                    {c.boardId !== boardId ? `${c.boardTitle} · ` : ""}
                    {c.current
                      ? file && file.version > 1
                        ? `Aktuelle Fassung (${file.version})`
                        : "Aktuelle Fassung"
                      : c.version
                        ? `Ältere Fassung (${c.version})`
                        : "Ältere Fassung"}
                    {c.archived ? " · archiviert" : ""}
                  </span>
                </span>
                {canEdit && !c.current && !file?.trashedAt && (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void update([c.attachmentId])}
                  >
                    Aktualisieren
                  </Button>
                )}
                {!c.archived && (onOpenCard || c.boardId !== boardId) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onClose();
                      if (onOpenCard && c.boardId === boardId) onOpenCard(c.id);
                      else navigate(`/b/${c.boardId}?card=${c.id}`);
                    }}
                  >
                    Öffnen
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm">Diese Datei wird in keiner Karte verwendet.</p>
        )}
        {canEdit && older.length > 1 && !file?.trashedAt && (
          <Button variant="primary" disabled={busy} onClick={() => void update()}>
            Alle {older.length} auf Fassung {file?.version} bringen
          </Button>
        )}
        <p className="text-xs text-[var(--ink-soft)]">
          Kartenanhänge behalten ihre Datei, auch wenn sie hier umbenannt,
          verschoben, gelöscht oder durch eine neue Fassung ersetzt wird. Erst
          „Aktualisieren“ tauscht den Anhang aus.
        </p>
      </div>
    </Sheet>
  );
}
